import { supabase } from '../lib/supabase';
import { getCurrentOrgId } from '../lib/getCurrentOrgId';

/**
 * subcontractorSpecialtiesService
 * Specialites d'un sous-traitant, partagees avec OnSiteBid : des codes
 * budgetaires (organization_budget_codes) lies a la fiche par
 * subcontractor_specialties. LECTURE SEULE ici : elles se modifient dans
 * OnSiteBid. RLS filtre par organisation.
 */

// « 09900 — Peinture », comme dans OnSiteBid.
const specialtyLabel = (code) => `${code?.code} — ${code?.name}`;

const subcontractorSpecialtiesService = {
  /**
   * Resume des specialites d'une fiche.
   * @returns {Promise<{ primary: string|null, others: string[] } | null>}
   *   primary : libelle de la specialite principale, null si « À définir ».
   *   others  : autres specialites, par ordre puis par code ; vide sans principale.
   *   null    : la lecture a echoue.
   */
  async getSummary(subcontractorId) {
    try {
      const orgId = await getCurrentOrgId();
      if (!orgId || !subcontractorId) return null;

      const links = await supabase
        ?.from('subcontractor_specialties')
        ?.select('budget_code_id, is_primary')
        ?.eq('subcontractor_id', subcontractorId)
        ?.eq('organization_id', orgId);
      if (links?.error) throw links?.error;
      const rows = links?.data || [];
      if (rows?.length === 0) return { primary: null, others: [] };

      const codes = await supabase
        ?.from('organization_budget_codes')
        ?.select('id, code, name, order_index')
        ?.eq('organization_id', orgId)
        ?.in('id', rows?.map((row) => row?.budget_code_id));
      if (codes?.error) throw codes?.error;

      // Un code inconnu de la compagnie est ignore a l'affichage.
      const byId = new Map((codes?.data || [])?.map((code) => [code?.id, code]));
      const known = rows
        ?.map((row) => ({ code: byId?.get(row?.budget_code_id), isPrimary: row?.is_primary === true }))
        ?.filter((item) => item?.code);
      const primary = known?.find((item) => item?.isPrimary)?.code || null;
      if (!primary) return { primary: null, others: [] };

      const others = known
        ?.filter((item) => item?.code?.id !== primary?.id)
        ?.map((item) => item?.code)
        ?.sort(
          (a, b) =>
            (a?.order_index ?? 0) - (b?.order_index ?? 0) ||
            String(a?.code)?.localeCompare(String(b?.code), 'fr')
        );
      return { primary: specialtyLabel(primary), others: others?.map(specialtyLabel) };
    } catch (error) {
      console.error('[subcontractorSpecialtiesService.getSummary]', error);
      return null;
    }
  },
};

export default subcontractorSpecialtiesService;
